import { useEffect, useState, useMemo, useContext, useRef } from "react";

import { TabView, TabPanel } from "primereact/tabview";
import { InputText } from "primereact/inputtext";
import { ScrollPanel } from "primereact/scrollpanel";
import { DataView } from "primereact/dataview";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Skeleton } from "primereact/skeleton";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { ContentCardSkeleton } from "sj-ui-toolkit/src/atlas/components/ContentCard";
import { PathCard } from "sj-ui-toolkit/src/atlas/components/ContentCard/PathCard/PathCard";
import { getColorForLevel } from "sj-ui-toolkit/src/atlas/utils/svgClasses";
import type { VariantType } from "sj-ui-toolkit/src/atlas/components/ContentCard/contentCardTypes";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

import { PathsContext } from "@/context/PathsContext";
import {
  loadFilteredPathsAsync,
  getActivityPathConflict,
  toggleActivated,
  type PathData,
} from "@/api/paths";
import { loadLevelsAsync } from "@/api/levels";
import ClonePathModal from "@/components/paths/ClonePathModal";
import { truncate } from "lodash";

// ---------------------------------------------------------------------------
// Constants (page-level — used only by this page)
// ---------------------------------------------------------------------------

const ROLE_KEYWORDS = [
  "learner",
  "developer",
  "engineer",
  "tester",
  "architect",
  "analyst",
  "specialist",
  "manager",
  "lead",
  "senior",
  "junior",
  "expert",
  "professional",
  "administrator",
  "consultant",
  "scientist",
  "researcher",
  "dev",
];

const NO_PATHS_FOUND = "There are no Active paths...";

const CONTENT_TYPES = [
  { value: "video", label: "Video" },
  { value: "handson", label: "Hands-on" },
  { value: "mixed", label: "Mixed" },
  { value: "activity", label: "Activity" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const categorizeAudiences = (paths: PathData[]) => {
  const allAudiences = new Set<string>();
  paths.forEach(path => path.audiences?.forEach(a => allAudiences.add(a.value)));

  const roles: string[] = [];
  const topics: string[] = [];

  allAudiences.forEach(audienceName => {
    const lower = audienceName.toLowerCase();
    if (ROLE_KEYWORDS.some(k => lower.includes(k))) {
      roles.push(audienceName);
    } else {
      topics.push(audienceName);
    }
  });

  roles.sort();
  topics.sort();

  return {
    roles: roles.map(name => ({ label: name, value: name })),
    topics: topics.map(name => ({ label: name, value: name })),
  };
};

interface LevelOption {
  label: string;
  value: number | string;
}

interface PathsState {
  defaultPaths: PathData[];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const PathsRecommended = () => {
  useBreadcrumb({
    label: "Recommended Paths",
    route: AdminAppRoutes.routes.paths.routes.recommended.route,
    display: "none",
    resetTrail: true,
  });

  const { showToast } = useToast();
  const { allPathNames, allPaths, loadPathsAsync, updatePathActivation } = useContext(PathsContext);

  const [data, setData] = useState<PathsState>({ defaultPaths: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<(number | string)[]>([]);
  const [showRetired, setShowRetired] = useState(false);
  const [levels, setLevels] = useState<LevelOption[]>([]);
  const [levelsLoading, setLevelsLoading] = useState(true);
  const [isLoadingToggle, setIsLoadingToggle] = useState(false);
  const [isCloneModalVisible, setIsCloneModalVisible] = useState(false);
  const [selectedClonePath, setSelectedClonePath] = useState<PathData | null>(null);
  const [contentType, setContentType] = useState<string | null>(null);

  const isMountedRef = useRef(false);

  useEffect(() => {
    if (isMountedRef.current) return;
    isMountedRef.current = true;
    void fetchLevels();
    void loadPathsAsync();
  }, []);

  useEffect(() => {
    if (isMountedRef.current) {
      void fetchFilteredPaths();
    }
  }, [contentType]);

  const fetchDefaultPaths = async () => {
    setIsLoading(true);
    try {
      const res = await loadFilteredPathsAsync({ path_type: "path", path_source: "default" });
      setData({ defaultPaths: res.paths ?? [] });
    } catch {
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to load paths",
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFilteredPaths = async () => {
    setIsLoading(true);
    if (contentType) {
      try {
        const res = await loadFilteredPathsAsync({
          path_type: "path",
          path_source: "default",
          content_type: contentType,
        });
        setData({ defaultPaths: res.paths ?? [] });
      } catch {
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to load filtered paths",
          life: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      await fetchDefaultPaths();
    }
  };

  const fetchLevels = async () => {
    setLevelsLoading(true);
    try {
      const result = await loadLevelsAsync();
      const levelOptions = result.courses
        .filter(l => l.name !== "Security Awareness")
        .map(l => ({ label: l.name, value: l.id }));
      setLevels(levelOptions);
    } catch (error) {
      console.error("Error loading levels:", error);
    } finally {
      setLevelsLoading(false);
    }
  };

  const filterData = (items: PathData[]) => {
    return items.filter(item => {
      if (!showRetired && item.retired === true) return false;

      const matchesSearch =
        !searchTerm ||
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const selectedAudienceValues = [...selectedRoles, ...selectedTopics];
      const matchesAudience =
        selectedAudienceValues.length === 0 ||
        (item.audiences?.some(a => selectedAudienceValues.includes(a.value)) ?? false);

      const matchesLevel =
        selectedLevels.length === 0 || selectedLevels.includes(item.levelId as number | string);

      return matchesSearch && matchesAudience && matchesLevel;
    });
  };

  const handleToggleActivated = async (item: PathData) => {
    const isActivating = !item.activated;

    if (isActivating) {
      const conflict = getActivityPathConflict(item.rankName, allPaths);
      if (conflict) {
        showToast({
          severity: "warn",
          summary: "Active path conflict",
          detail: `"${conflict.name}" is the currently active ${item.levelName ?? item.rankName} path. Deactivate it before activating "${item.name}".`,
          life: 8000,
        });
        return;
      }
    }

    setIsLoadingToggle(true);
    await toggleActivated({
      id: item.id!,
      paths: data.defaultPaths,
      type: "recommended",
      setData: setData as React.Dispatch<
        React.SetStateAction<{
          customPaths?: PathData[];
          defaultPaths?: PathData[];
          quests?: PathData[];
        }>
      >,
      onSuccess: () => updatePathActivation(item.id!, isActivating),
      onRevert: () => updatePathActivation(item.id!, !isActivating),
      showToast,
    });
    setIsLoadingToggle(false);
  };

  const audienceOptions = useMemo(() => {
    if (data.defaultPaths.length === 0) return { roles: [], topics: [] };
    return categorizeAudiences(data.defaultPaths);
  }, [data.defaultPaths]);

  const filteredData = useMemo(
    () => ({
      active: filterData(data.defaultPaths.filter(item => item.activated)),
      inactive: filterData(data.defaultPaths.filter(item => !item.activated)),
    }),
    [data, searchTerm, selectedRoles, selectedTopics, selectedLevels, showRetired]
  );

  return (
    <div className="w-full">
      <div className="mb-4">
        <h1>Recommended Paths</h1>
      </div>

      <div className="py-8">
        <div className="flex justify-between flex-wrap align-center gap-4 mb-4">
          <div className="flex flex-wrap gap-4">
            <InputText
              value={searchTerm}
              disabled={isLoading}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search Paths"
              aria-label="Search paths"
            />
            <MultiSelect
              value={selectedRoles}
              options={audienceOptions.roles}
              disabled={isLoading}
              onChange={e => setSelectedRoles(e.value as string[])}
              optionLabel="label"
              placeholder="Select Roles"
              display="chip"
              showClear
              maxSelectedLabels={1}
              className="w-72"
              aria-label="Filter by role"
            />
            <MultiSelect
              value={selectedLevels}
              options={levels}
              disabled={isLoading || levelsLoading}
              onChange={e => setSelectedLevels(e.value as (number | string)[])}
              optionLabel="label"
              placeholder="Select Levels"
              display="chip"
              showClear
              maxSelectedLabels={1}
              className="w-72"
              aria-label="Filter by level"
            />
            <MultiSelect
              value={selectedTopics}
              options={audienceOptions.topics}
              disabled={isLoading}
              onChange={e => setSelectedTopics(e.value as string[])}
              optionLabel="label"
              placeholder="Select Topics"
              display="chip"
              showClear
              maxSelectedLabels={1}
              className="w-72"
              aria-label="Filter by topic"
            />
            <Dropdown
              value={contentType ?? null}
              options={CONTENT_TYPES}
              disabled={isLoading}
              onChange={e => setContentType((e.value as string | null) || null)}
              optionLabel="label"
              placeholder="Select Content Type"
              showClear
              className="w-72"
              aria-label="Filter by content type"
            />
          </div>
          <div className="flex items-center">
            <Checkbox
              inputId="showRetired"
              checked={showRetired}
              onChange={e => setShowRetired(e.checked ?? false)}
            />
            <label htmlFor="showRetired" className="ml-2 body-sm">
              Show retired
            </label>
          </div>
        </div>
      </div>

      <TabView
        aria-label="Recommended paths"
        activeIndex={activeTabIndex}
        onTabChange={e => setActiveTabIndex(e.index)}
      >
        <TabPanel header="Active">
          <ScrollPanel
            style={{ width: "100%", height: "70vh" }}
            pt={{ barY: { style: { backgroundColor: "var(--primary-color)" } } }}
          >
            <CatalogTabContent
              data={filteredData.active}
              isLoading={isLoading}
              toggleActivated={handleToggleActivated}
              isLoadingToggle={isLoadingToggle}
              onClone={path => {
                setSelectedClonePath(path);
                setIsCloneModalVisible(true);
              }}
            />
          </ScrollPanel>
        </TabPanel>
        <TabPanel header="Inactive">
          <ScrollPanel
            style={{ width: "100%", height: "70vh" }}
            pt={{ barY: { style: { backgroundColor: "var(--primary-color)" } } }}
          >
            <CatalogTabContent
              data={filteredData.inactive}
              isLoading={isLoading}
              toggleActivated={handleToggleActivated}
              isLoadingToggle={isLoadingToggle}
              onClone={path => {
                setSelectedClonePath(path);
                setIsCloneModalVisible(true);
              }}
            />
          </ScrollPanel>
        </TabPanel>
      </TabView>

      <ClonePathModal
        visible={isCloneModalVisible}
        onHide={() => setIsCloneModalVisible(false)}
        originalPath={selectedClonePath}
        existingPathNames={allPathNames}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab content sub-component
// ---------------------------------------------------------------------------

interface CatalogTabContentProps {
  data: PathData[];
  isLoading: boolean;
  toggleActivated: (item: PathData) => void;
  isLoadingToggle: boolean;
  onClone: (path: PathData) => void;
}

const CatalogTabContent = ({
  data,
  isLoading,
  toggleActivated,
  isLoadingToggle,
  onClone,
}: CatalogTabContentProps) => {
  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="mb-4">
          <Skeleton width="10rem" height="1rem" />
        </div>
        <div className="flex flex-wrap gap-6">
          {Array(8)
            .fill(null)
            .map((_, idx) => (
              <ContentCardSkeleton key={idx} cardSize="small" showActivated />
            ))}
        </div>
      </div>
    );
  }

  const getRolesForPath = (path: PathData) => {
    const allAudiences: string[] = [];
    path.audiences?.forEach(a => allAudiences.push(a.value));
    return allAudiences;
  };

  const listTemplate = (items: PathData[]) => {
    const groupedItems: Record<string, PathData[]> = {};

    items.forEach(item => {
      const audiences = getRolesForPath(item);
      const primaryAudience = audiences.length > 0 ? [...audiences].sort()[0]! : "";
      if (!groupedItems[primaryAudience]) groupedItems[primaryAudience] = [];
      groupedItems[primaryAudience]!.push(item);
    });

    const sortedAudiences = Object.keys(groupedItems).sort((a, b) => {
      if (a === "") return 1;
      if (b === "") return -1;
      return a.localeCompare(b);
    });

    sortedAudiences.forEach(audience => {
      groupedItems[audience]!.sort(
        (a, b) => ((a.levelId as number) ?? 999) - ((b.levelId as number) ?? 999)
      );
    });

    return (
      <div className="space-y-8">
        {sortedAudiences.map(audience => (
          <div key={audience}>
            <h3 className="atlas-h5 mb-4">{audience || "Other"}</h3>
            <div className="flex flex-wrap gap-6">
              {groupedItems[audience]!.map(item => (
                <PathCard
                  key={item.id}
                  contentType="path"
                  lessonType={undefined}
                  cardSize="small"
                  id={String(item.id ?? "")}
                  name={truncate(item.name, { length: 50 })}
                  description={truncate(item.description, { length: 120 })}
                  lessonsInEnrollment={item.lessonsCount}
                  estimatedTimeToComplete={item.totalDuration}
                  points={item.totalPoints ?? 0}
                  variant={getColorForLevel(item.rankName ?? "") as VariantType}
                  showActivated
                  activated={item.activated}
                  contentTypeTag={item.contentType}
                  onActivate={() => toggleActivated(item)}
                  isLoadingToggle={isLoadingToggle}
                  onClone={() => onClone(item)}
                  restricted={item.restricted}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <DataView
      value={data}
      listTemplate={listTemplate}
      layout="list"
      emptyMessage={NO_PATHS_FOUND}
    />
  );
};

export default PathsRecommended;
